import { BaseComponent } from '../lib/BaseComponent.js';
import { api, type BillingInfo } from '../lib/api.js';
import { router } from '../lib/router.js';
import { Toast } from './shared/Toast.js';

/**
 * Billing Page - plan, usage, trial status, Stripe Checkout and Customer Portal.
 */
export class BillingPage extends BaseComponent {
  private info: BillingInfo | null = null;
  private error = '';
  private busy = false;
  private interval: 'month' | 'year' = 'month';

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'billing-container' });
    const header = this.createElement('header', { className: 'billing-header' });
    const back = this.createElement('a', { href: '/dashboard', className: 'back-link' }, '← Dashboard');
    header.appendChild(back);
    header.appendChild(this.createElement('h1', {}, 'Plan & billing'));
    container.appendChild(header);

    const main = this.createElement('main', { className: 'billing-main' });
    container.appendChild(main);
    this.shadow.appendChild(container);

    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      Toast.success('Thanks! Your subscription is active. It can take a few seconds to appear here.');
    } else if (params.get('checkout') === 'cancel') {
      Toast.info('Checkout cancelled. Your trial continues.');
    }

    if (!this.info && !this.error) {
      main.appendChild(this.createElement('p', { className: 'muted' }, 'Loading plan…'));
      try {
        this.info = await api.getBilling();
      } catch (e) {
        this.error = (e as Error).message || 'Could not load billing';
      }
      this.rerender();
      return;
    }

    if (this.error) {
      main.appendChild(this.createElement('div', { className: 'notice notice-err' }, this.error));
      return;
    }

    const ent = this.info!.entitlements;
    const isAdmin = api.currentUser()?.role === 'admin';

    // Status card
    const status = this.createElement('section', { className: 'card status-card' });
    status.appendChild(this.createElement('div', { className: 'eyebrow' }, 'Current plan'));
    status.appendChild(this.createElement('h2', {}, ent.isTrial ? `${ent.planName} (${ent.status === 'expired' ? 'ended' : `${ent.trialDaysLeft} days left`})` : `${ent.planName} · ${ent.status.replace('_', ' ')}`));
    const usage = this.createElement('p', { className: 'usage' });
    const pct = Math.min(100, Math.round((ent.activeExtinguishers / Math.max(1, ent.maxExtinguishers)) * 100));
    usage.textContent = `${ent.activeExtinguishers.toLocaleString()} of ${ent.maxExtinguishers === Number.MAX_SAFE_INTEGER ? 'unlimited' : ent.maxExtinguishers.toLocaleString()} active extinguishers`;
    status.appendChild(usage);
    const bar = this.createElement('div', { className: 'bar' });
    const fill = this.createElement('div', { className: `bar-fill${pct >= 90 ? ' hot' : ''}` });
    fill.style.width = `${pct}%`;
    bar.appendChild(fill);
    status.appendChild(bar);
    if (ent.blockReason) status.appendChild(this.createElement('div', { className: 'notice notice-warn' }, ent.blockReason));
    else if (ent.overLimit && ent.graceEndsAt) status.appendChild(this.createElement('div', { className: 'notice notice-warn' }, `You are over your plan's limit. You can keep adding units until ${new Date(ent.graceEndsAt).toLocaleDateString()}; upgrade before then.`));
    if (ent.cancelAt) status.appendChild(this.createElement('p', { className: 'muted' }, `Cancellation scheduled for ${new Date(ent.cancelAt).toLocaleDateString()}. Records stay exportable for 90 days after.`));
    if (ent.currentPeriodEnd && !ent.cancelAt && !ent.isTrial) status.appendChild(this.createElement('p', { className: 'muted' }, `Renews ${new Date(ent.currentPeriodEnd).toLocaleDateString()} (${ent.interval === 'year' ? 'annual' : 'monthly'}).`));
    if (ent.hasStripeCustomer && isAdmin && this.info!.billingEnabled) {
      const portalBtn = this.createElement('button', { className: 'btn-secondary' }, 'Manage billing, invoices, and payment method');
      portalBtn.addEventListener('click', () => this.openPortal());
      status.appendChild(portalBtn);
    }
    main.appendChild(status);

    // Plans
    const plansSection = this.createElement('section');
    plansSection.appendChild(this.createElement('h2', {}, ent.isTrial || ent.status === 'canceled' ? 'Choose a plan' : 'Change plan'));
    plansSection.appendChild(this.createElement('p', { className: 'muted' }, 'Every plan is the full product with unlimited users and locations. Only the extinguisher limit changes.'));

    if (!this.info!.billingEnabled) {
      plansSection.appendChild(this.createElement('div', { className: 'notice notice-info' }, 'Online billing is being enabled. To subscribe now, email hello@fireguardhq.com and we will invoice you.'));
    }
    if (!isAdmin) {
      plansSection.appendChild(this.createElement('div', { className: 'notice notice-info' }, 'Only account admins can change the plan.'));
    }

    const toggle = this.createElement('div', { className: 'toggle' });
    for (const [val, label] of [['month', 'Monthly'], ['year', 'Annual · 2 months free']] as const) {
      const b = this.createElement('button', { className: `toggle-btn${this.interval === val ? ' active' : ''}`, type: 'button' }, label);
      b.addEventListener('click', () => {
        this.interval = val;
        this.rerender();
      });
      toggle.appendChild(b);
    }
    plansSection.appendChild(toggle);

    const hinted = new URLSearchParams(window.location.search).get('plan');
    const grid = this.createElement('div', { className: 'plans' });
    for (const p of this.info!.plans) {
      const isHinted = hinted === p.id && ent.isTrial;
      const card = this.createElement('div', { className: `card plan${p.id === ent.planId || isHinted ? ' current' : ''}` });
      if (isHinted) card.appendChild(this.createElement('span', { className: 'badge' }, 'Selected on pricing page'));
      card.appendChild(this.createElement('h3', {}, p.name));
      card.appendChild(this.createElement('p', { className: 'muted' }, p.tagline));
      const price = this.interval === 'year' ? Math.round(p.annualUsd / 12) : p.monthlyUsd;
      card.appendChild(this.createElement('div', { className: 'price' }, `$${price}/mo`));
      card.appendChild(this.createElement('p', { className: 'muted small' }, this.interval === 'year' ? `$${p.annualUsd.toLocaleString()} billed yearly` : 'Billed monthly'));
      card.appendChild(this.createElement('p', { className: 'limit' }, `Up to ${p.maxExtinguishers.toLocaleString()} active extinguishers`));
      if (p.id === ent.planId && !ent.isTrial) {
        card.appendChild(this.createElement('span', { className: 'badge' }, 'Current plan'));
      } else if (isAdmin && this.info!.billingEnabled && (ent.isTrial || ent.status === 'canceled')) {
        const btn = this.createElement('button', { className: 'btn-primary' }, this.busy ? 'Redirecting…' : `Subscribe to ${p.name}`);
        if (this.busy) btn.setAttribute('disabled', 'true');
        btn.addEventListener('click', () => this.checkout(p.id));
        card.appendChild(btn);
      } else if (isAdmin && this.info!.billingEnabled) {
        card.appendChild(this.createElement('p', { className: 'muted small' }, 'Switch plans from the billing portal above.'));
      }
      grid.appendChild(card);
    }
    plansSection.appendChild(grid);
    plansSection.appendChild(this.createElement('p', { className: 'muted small' }, `More than 1,000 extinguishers: blocks of ${this.info!.overage.blockSize} at $${this.info!.overage.monthlyUsd}/month each, or contact us for Enterprise.`));
    main.appendChild(plansSection);
  }

  private async checkout(planId: string): Promise<void> {
    this.busy = true;
    this.rerender();
    try {
      const { url } = await api.startCheckout(planId, this.interval);
      window.location.href = url;
    } catch (e) {
      Toast.error((e as Error).message || 'Could not start checkout');
      this.busy = false;
      this.rerender();
    }
  }

  private async openPortal(): Promise<void> {
    try {
      const { url } = await api.openBillingPortal();
      window.location.href = url;
    } catch (e) {
      Toast.error((e as Error).message || 'Could not open billing portal');
    }
  }

  private rerender(): void {
    this.shadow.replaceChildren();
    void this.render();
  }

  private getStyles(): string {
    return `
      .billing-container { max-width: 960px; margin: 0 auto; padding: 24px; }
      .billing-header { display: flex; flex-direction: column; gap: 8px; margin-bottom: 24px; }
      .billing-header h1 { font-size: 28px; }
      .back-link { color: var(--primary); text-decoration: none; font-size: 14px; }
      .billing-main { display: grid; gap: 28px; }
      .card { background: #fff; border: 1px solid var(--gray-200); border-radius: var(--border-radius); padding: 22px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
      .status-card h2 { font-size: 22px; margin: 4px 0 8px; }
      .eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--gray-500); font-weight: 600; }
      .usage { color: var(--gray-700); margin-bottom: 8px; }
      .bar { height: 10px; background: var(--gray-100); border-radius: 999px; overflow: hidden; margin-bottom: 14px; }
      .bar-fill { height: 100%; background: var(--success); transition: width 0.3s; }
      .bar-fill.hot { background: var(--danger); }
      .muted { color: var(--gray-500); }
      .small { font-size: 13px; }
      .notice { border-radius: var(--border-radius); padding: 12px 14px; border: 1px solid; margin: 10px 0; font-size: 14px; }
      .notice-err { border-color: #fecaca; background: #fef2f2; color: #b91c1c; }
      .notice-warn { border-color: #fde68a; background: #fffbeb; color: #92400e; }
      .notice-info { border-color: #bfdbfe; background: #eff6ff; color: #1e40af; }
      .toggle { display: inline-flex; border: 1px solid var(--gray-300); border-radius: 999px; overflow: hidden; margin: 14px 0 18px; }
      .toggle-btn { padding: 8px 16px; border: none; background: #fff; font-size: 14px; cursor: pointer; color: var(--gray-700); }
      .toggle-btn.active { background: var(--gray-900); color: #fff; }
      .plans { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; }
      .plan { display: flex; flex-direction: column; gap: 6px; position: relative; }
      .plan.current { border-color: var(--primary); box-shadow: 0 0 0 2px rgba(59,130,246,0.15); }
      .plan h3 { font-size: 18px; }
      .price { font-size: 30px; font-weight: 700; margin-top: 6px; }
      .limit { font-family: ui-monospace, Menlo, monospace; font-size: 13px; background: var(--gray-50); border: 1px solid var(--gray-200); border-radius: 4px; padding: 6px 8px; }
      .badge { display: inline-block; background: var(--primary); color: #fff; font-size: 12px; font-weight: 600; padding: 4px 8px; border-radius: 4px; margin-top: auto; align-self: flex-start; }
      .btn-primary { margin-top: auto; padding: 10px 14px; background: var(--primary); color: #fff; border: none; border-radius: var(--border-radius); font-size: 15px; font-weight: 500; cursor: pointer; }
      .btn-primary:hover:not(:disabled) { background: var(--primary-dark); }
      .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
      .btn-secondary { padding: 10px 14px; background: #fff; color: var(--gray-900); border: 1px solid var(--gray-300); border-radius: var(--border-radius); font-size: 14px; cursor: pointer; }
      .btn-secondary:hover { background: var(--gray-50); }
    `;
  }
}

customElements.define('billing-page', BillingPage);
